import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Star, MessageSquare, Phone, CheckCircle2, ArrowRight } from "lucide-react";
import Navbar from "@/components/campus/Navbar";
import Footer from "@/components/campus/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function CollegeDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.getCollege(slug).then(setData).catch(()=>toast.error("College not found")); }, [slug]);

  if (!data) return <div className="min-h-screen bg-[#0F172A] text-white flex items-center justify-center">Loading...</div>;

  const startBooking = (amb, type) => {
    if (!user) { toast.info("Please log in to continue"); nav(`/auth?mode=login&next=/college/${slug}`); return; }
    if (user.role !== "student") { toast.error("Only students can book conversations"); return; }
    nav(`/book?ambassador=${amb.user_id}&college=${encodeURIComponent(data.name)}&type=${type}`);
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <Navbar/>
      <section className="relative h-[420px] mt-16">
        <img src={data.banner_photo_url || "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1600"} alt={data.name} className="w-full h-full object-cover" loading="lazy"/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/60 to-transparent"/>
        <div className="absolute bottom-8 left-0 right-0 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <h1 className="font-display text-4xl md:text-6xl font-bold">{data.name}</h1>
            <div className="flex flex-wrap items-center gap-3 mt-3 text-slate-300">
              <span className="flex items-center gap-1"><MapPin size={14}/>{data.city}, {data.state}</span>
              {data.naac_grade && <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 font-semibold">NAAC {data.naac_grade}</span>}
              <span className="text-xs px-2 py-1 rounded-full bg-blue-500/15 text-blue-300 font-semibold capitalize">{data.college_type}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="font-display text-2xl font-bold mb-3">About</h2>
              <p className="text-slate-300 leading-relaxed">{data.description}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {(data.streams || []).map(s => <span key={s} className="text-xs px-3 py-1 rounded-full bg-white/5 border border-white/10">{s}</span>)}
              </div>
            </div>

            <div>
              <h2 className="font-display text-2xl font-bold mb-4">Talk to someone who studies here</h2>
              {(data.ambassadors || []).length === 0 && <div className="text-slate-400 text-sm">No verified Campus Ambassadors yet. Check back soon!</div>}
              <div className="grid md:grid-cols-2 gap-4">
                {(data.ambassadors || []).map((a) => (
                  <div key={a.user_id} data-testid={`ambassador-${a.user_id}`} className="glass rounded-2xl p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#2563EB] to-[#1E3A8A] flex items-center justify-center font-bold">{(a.full_name||"A").split(" ").map(w=>w[0]).slice(0,2).join("")}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold flex items-center gap-1.5">{a.full_name} <CheckCircle2 size={14} className="text-emerald-400"/></div>
                        <div className="text-xs text-slate-400">{a.course} · {a.year} Year</div>
                        <div className="flex items-center gap-2 mt-1 text-xs">
                          <span className="flex items-center gap-1 text-amber-400"><Star size={12} className="fill-amber-400"/> {a.rating_avg || 0}</span>
                          <span className="text-slate-500">· {(a.total_calls||0)+(a.total_chats||0)} convos</span>
                          <span className={`w-2 h-2 rounded-full ${a.status==="active"?"bg-emerald-400":a.status==="busy"?"bg-amber-400":"bg-slate-500"}`}/>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-slate-300 mt-3 line-clamp-2">{a.bio}</p>
                    <div className="grid grid-cols-2 gap-2 mt-4">
                      <button data-testid={`chat-${a.user_id}`} onClick={()=>startBooking(a, "chat")} className="px-3 py-2 rounded-lg border border-[#2563EB]/50 text-[#60A5FA] hover:bg-[#2563EB]/10 text-sm font-semibold inline-flex items-center justify-center gap-1.5"><MessageSquare size={14}/>Chat — ₹49</button>
                      <button data-testid={`call-${a.user_id}`} onClick={()=>startBooking(a, "audio_call")} className="px-3 py-2 rounded-lg bg-[#2563EB] hover:bg-blue-600 text-sm font-semibold inline-flex items-center justify-center gap-1.5"><Phone size={14}/>Call — ₹99</button>
                    </div>
                    <div className="text-[10px] text-emerald-400 mt-2 text-center">FREE with FIRSTCOLLEGE · 50% off with FIRSTCALL</div>
                  </div>
                ))}
              </div>
            </div>

            {(data.reviews||[]).length>0 && (
              <div>
                <h2 className="font-display text-2xl font-bold mb-4">What students found out</h2>
                <div className="space-y-3">
                  {data.reviews.map((r,i)=>(
                    <div key={i} className="glass rounded-xl p-4">
                      <div className="flex gap-1 text-amber-400 mb-1">{Array.from({length:r.stars||5}).map((_,k)=><Star key={k} size={12} className="fill-amber-400"/>)}</div>
                      {r.review_text && <p className="text-slate-200">{r.review_text}</p>}
                      <div className="text-xs text-slate-500 mt-1">— {r.student_stream || "Student"} from {r.student_state || "India"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="lg:sticky lg:top-24 h-fit">
            <div className="glass rounded-2xl p-6">
              <h3 className="font-display text-lg font-bold mb-2">Made your decision?</h3>
              <p className="text-slate-400 text-sm mb-5">Our team will handle your entire admission process — completely free.</p>
              <Link to={`/decided/${slug}`} data-testid="decided-cta" className="block text-center px-5 py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold hover:bg-amber-400 transition">I've Decided — Get Free Admission Help →</Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Mobile sticky bar */}
      <div className="lg:hidden fixed bottom-20 left-0 right-0 px-4 z-30">
        <Link to={`/decided/${slug}`} className="block text-center px-5 py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold shadow-2xl">I've Decided — I Want Admission Here</Link>
      </div>

      <Footer/>
    </div>
  );
}
